import React from "react";
import { useLanguage } from "context/LanguageContext";

export type MultiSelectOption = {
  value: string;
  label: string;
  description?: string;
};

type MultiSelectFieldProps = {
  label: string;
  options: MultiSelectOption[];
  values: string[];
  onChange: (nextValues: string[]) => void;
  placeholder?: string;
  emptyText?: string;
  loading?: boolean;
  helperText?: string;
  required?: boolean;
  className?: string;
};

const MultiSelectField = ({
  label,
  options,
  values,
  onChange,
  placeholder,
  emptyText,
  loading = false,
  helperText,
  required = false,
  className = "",
}: MultiSelectFieldProps) => {
  const { t } = useLanguage();
  const [keyword, setKeyword] = React.useState("");

  const filteredOptions = React.useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    if (!normalizedKeyword) return options;

    return options.filter((option) => {
      const labelText = option.label.toLowerCase();
      const descriptionText = (option.description ?? "").toLowerCase();
      return (
        labelText.includes(normalizedKeyword) ||
        descriptionText.includes(normalizedKeyword)
      );
    });
  }, [keyword, options]);

  const selectedOptions = React.useMemo(
    () => options.filter((option) => values.includes(option.value)),
    [options, values]
  );

  const toggleValue = (value: string) => {
    const hasValue = values.includes(value);
    if (hasValue) {
      onChange(values.filter((item) => item !== value));
      return;
    }
    onChange([...values, value]);
  };

  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-ink">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </label>

      <div className="space-y-2 rounded-lg border border-line p-3">
        <input
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          placeholder={placeholder ?? t("navbar.search")}
          className="w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface-muted"
        />

        <div className="max-h-[180px] space-y-1 overflow-y-auto pr-1">
          {loading ? (
            <p className="py-4 text-center text-xs text-ink-muted">
              Dang tai danh sach...
            </p>
          ) : filteredOptions.length === 0 ? (
            <p className="py-4 text-center text-xs text-ink-muted">
              {emptyText ?? t("state.noData")}
            </p>
          ) : (
            filteredOptions.map((option) => {
              const checked = values.includes(option.value);
              return (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-start justify-between rounded-md px-2 py-1.5 hover:bg-surface-hover"
                >
                  <div className="flex min-w-0 items-start gap-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleValue(option.value)}
                      className="mt-1"
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">
                        {option.label}
                      </p>
                      {option.description ? (
                        <p className="truncate text-xs text-ink-muted">
                          {option.description}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-line pt-2">
          <span className="text-xs font-medium text-ink-muted">
            Da chon: {selectedOptions.length}
          </span>
          {selectedOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => toggleValue(option.value)}
              className="inline-flex items-center gap-1 rounded-full bg-warning-100 px-2.5 py-1 text-xs font-medium text-warning-700 hover:bg-warning-200 dark:bg-warning-900/20 dark:text-warning-300"
            >
              {option.label}
              <span aria-hidden>x</span>
            </button>
          ))}
        </div>
      </div>

      {helperText ? (
        <p className="mt-1 text-xs text-ink-muted">
          {helperText}
        </p>
      ) : null}
    </div>
  );
};

export default MultiSelectField;
